import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Upload, Layers, FileInput, LogOut, Settings, Shield, ShieldAlert, User, LayoutDashboard, Cog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { assetUrl } from "@/lib/api";
import { JudgeBadge, RoleBadge } from "@/components/common/Badges";
import { NotificationBell } from "@/components/layout/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link", end: true },
  { to: "/library", label: "Custom Cards", testid: "nav-library-link" },
  { to: "/official", label: "Official Cards", testid: "nav-official-link" },
  { to: "/decks", label: "Deck Builder", testid: "nav-decks-link" },
  { to: "/sets", label: "Custom Sets", testid: "nav-sets-link" },
  { to: "/card-backs", label: "Card Backs", testid: "nav-card-backs-link" },
  { to: "/mechanics", label: "Mechanics", testid: "nav-mechanics-link" },
  { to: "/communities", label: "Communities", testid: "nav-communities-link" },
  { to: "/tournaments", label: "Tournaments", testid: "nav-tournaments-link" },
  { to: "/leagues", label: "Leagues", testid: "nav-leagues-link" },
  { to: "/companion", label: "Companion", testid: "nav-companion-link" },
  { to: "/dashboard", label: "My Workshop", testid: "nav-workshop-link" },
  { to: "/developers", label: "Developer API", testid: "nav-developers-link" },
];

const linkCls = ({ isActive }) =>
  `relative whitespace-nowrap px-3 py-2.5 text-[13px] font-medium transition-colors duration-200 after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:rounded-full after:transition-opacity ${isActive ? "text-aether-300 after:bg-aether-400 after:opacity-100 after:shadow-[0_0_8px_rgba(34,211,238,0.8)]" : "text-slate-300 after:opacity-0 hover:text-amber-200"}`;

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-amber-500/60 bg-gradient-to-b from-amber-700/40 to-copper-800/40 shadow-[0_0_14px_rgba(34,211,238,0.25)]">
        <Cog className="h-5 w-5 text-aether-300 motion-safe:animate-[spin_18s_linear_infinite]" aria-hidden />
      </span>
      <span className="leading-tight">
        <span className="block font-display text-base font-bold tracking-wide text-amber-100 sm:text-lg">The Planar Foundry</span>
        <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-copper-300/80 sm:block">Forge Your Cards. Build Your Legends. Shape the Multiverse.</span>
      </span>
    </Link>
  );
}

function UserMenu() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full border border-amber-900/50 p-0.5 pr-3 transition-colors hover:border-amber-500/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-testid="user-menu-trigger">
          {user.avatar_url ? (
            <img src={assetUrl(user.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-900/40 font-heading text-sm text-amber-200">{user.display_name[0]}</span>
          )}
          <span className="hidden max-w-[120px] truncate text-sm text-slate-200 lg:inline">{user.display_name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 border-[#2A3447] bg-[#121620] text-slate-200">
        <DropdownMenuLabel className="space-y-1.5">
          <p className="truncate text-sm">@{user.username}</p>
          <div className="flex flex-wrap gap-1"><RoleBadge role={user.role} /><JudgeBadge level={user.judge_level} /></div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#2A3447]" />
        <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="user-menu-dashboard"><LayoutDashboard className="mr-2 h-4 w-4" />My Workshop</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/u/${user.username}`)} data-testid="user-menu-profile"><User className="mr-2 h-4 w-4" />Public profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings"><Settings className="mr-2 h-4 w-4" />Account settings</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/me/moderation")} data-testid="user-menu-moderation"><ShieldAlert className="mr-2 h-4 w-4" />Moderation &amp; appeals</DropdownMenuItem>
        {can("users.view") && (
          <DropdownMenuItem onClick={() => navigate("/staff")} data-testid="user-menu-staff"><Shield className="mr-2 h-4 w-4" />Staff console</DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-[#2A3447]" />
        <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UploadMenu() {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="btn-gold px-4 py-2" data-testid="nav-upload-trigger"><Upload className="h-4 w-4" />Upload</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 border-[#2A3447] bg-[#121620] text-slate-200">
        <DropdownMenuItem onClick={() => navigate("/upload")} data-testid="nav-upload-single"><Upload className="mr-2 h-4 w-4" />Single card</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/upload/bulk")} data-testid="nav-upload-bulk"><Layers className="mr-2 h-4 w-4" />Bulk upload / set</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/import")} data-testid="nav-import"><FileInput className="mr-2 h-4 w-4" />Import cards / set</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/import#drafts")} data-testid="nav-import-drafts"><FileInput className="mr-2 h-4 w-4" />Import drafts</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/card-backs?upload=1")} data-testid="nav-upload-card-back"><Cog className="mr-2 h-4 w-4" />Card back</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ user }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button className="btn-ghost lg:hidden" aria-label="Open menu" data-testid="nav-mobile-menu-button"><Menu className="h-5 w-5" /></button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72 border-[#2A3447] bg-[#0B0D12] text-slate-200">
        <SheetTitle className="font-heading text-amber-100">Navigate</SheetTitle>
        <nav className="mt-6 flex flex-col gap-1">
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={`mobile-${l.testid}`}>{l.label}</NavLink>)}
          {user && <NavLink to="/upload" className={linkCls} data-testid="mobile-nav-upload-link">Upload card</NavLink>}
          {user && <NavLink to="/upload/bulk" className={linkCls} data-testid="mobile-nav-bulk-link">Bulk upload</NavLink>}
          {user && <NavLink to="/import" className={linkCls} data-testid="mobile-nav-import-link">Import cards / set</NavLink>}
          {!user && <NavLink to="/login" className={linkCls} data-testid="mobile-nav-login-link">Sign in</NavLink>}
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export function Navbar() {
  const { user, can } = useAuth();
  return (
    <header className="sticky top-0 z-50 border-b border-amber-700/40 bg-[#0A1222]/90 backdrop-blur-xl" data-testid="site-header">
      <div className="container-x flex h-16 items-center justify-between gap-4">
        <Logo />
        <div className="flex items-center gap-3">
          {user && can("users.view") && <Link to="/staff" className="btn-ghost hidden text-xs md:inline-flex" data-testid="nav-staff-link"><Shield className="h-4 w-4 text-copper-300" />Staff</Link>}
          {user && <div className="hidden sm:block"><UploadMenu /></div>}
          {user && <NotificationBell />}
          {user ? <UserMenu /> : user === false && (
            <div className="hidden items-center gap-2 sm:flex">
              <Link to="/login" className="btn-ghost" data-testid="nav-login-link">Sign in</Link>
              <Link to="/register" className="btn-gold px-4 py-2" data-testid="nav-register-link">Join the Foundry</Link>
            </div>
          )}
          <MobileMenu user={user} />
        </div>
      </div>
      <nav className="hidden border-t border-[#2A3447]/70 bg-[#0F1A2E]/80 lg:block" aria-label="Primary">
        <div className="container-x flex flex-wrap items-center justify-center gap-x-3">
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={l.testid}>{l.label}</NavLink>)}
        </div>
      </nav>
    </header>
  );
}
